import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { crossTabStorage } from '@/shared/utils/crossTabStorage';

/**
 * Client-portal session flag — the portal's equivalent of `useUserStore`,
 * and deliberately a SEPARATE store rather than a role on that one.
 *
 * Two reasons, one of which is a bug we would otherwise be inviting.
 *
 * 1. `resetAllStores()` (called on advisor logout) would clear a shared store,
 *    so an advisor signing out in one tab would sign a client out in another.
 *    The two populations use the same browser more often than you would think:
 *    an advisor demoing the portal to a client sitting next to them.
 *
 * 2. The advisor router gate reads `useUserStore.isAuthenticated`. If a portal
 *    sign-in set that flag, a client's browser would walk straight into the
 *    protected tree, render the sidebar, and fire every dashboard prefetch —
 *    all of which the API would refuse with 403, but the client would have
 *    spent a second looking at an advisor product before it emptied out.
 *
 * Like `useUserStore`, this holds a CLIENT-SIDE GUESS. It outlives the
 * HttpOnly cookie it describes, so the route gate proves the session with a
 * real `/portal/me` request before rendering anything.
 */
interface PortalSessionState {
  isAuthenticated: boolean;
  /** The signed-in client's display name, for the header before /me lands. */
  displayName: string | null;

  setAuthenticated: (displayName?: string | null) => void;
  logout: () => void;
}

export const usePortalSessionStore = create<PortalSessionState>()(
  devtools(
    persist(
      (set) => ({
        isAuthenticated: false,
        displayName: null,

        setAuthenticated: (displayName = null) =>
          set(
            { isAuthenticated: true, displayName },
            false,
            'portal/setAuthenticated',
          ),

        logout: () =>
          set(
            { isAuthenticated: false, displayName: null },
            false,
            'portal/logout',
          ),
      }),
      { name: 'portal-session', storage: crossTabStorage },
    ),
    { name: 'PortalSessionStore' },
  ),
);
